/* eslint-disable react-hooks/exhaustive-deps */
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { MapContext, MapContextProvider } from '../context/MapContext';
import { UiStateContext } from '../context/UiStateContext';
import { useDataLayerPush } from '../hooks/useDataLayerPush';
import BranchMapMarker from '../images/maps/marker-branch.png';
import { useGet_BranchLazyQuery } from '../types/graphqlHelpers';
import { Kfh_BranchType, Kfh_BranchTypeConnection, Kfh_UmbracoBranchFinderComponentType } from '../types/graphqlTypes';
import { updateCallTracking } from '../utils';
import { Alert } from './Alert';
import { BranchBlade } from './branches/BranchBlade';
import { Button } from './Button';
import { ExpanderPanel } from './ExpanderPanel';
import { ExpanderPanelGroup } from './ExpanderPanelGroup';
import { Input } from './forms/Input';
import { ListView } from './icon/icons/ListView';
import { MapView } from './icon/icons/MapView';
import { Search } from './icon/icons/Search';
import { IconLink } from './IconLink';
import { Loader } from './Loader';
import { Map, MapMarker } from './Map';
import { Panel } from './Panel';
import { Section } from './Section';
import { Spacer } from './Spacer';
import { Typography } from './Typography';

type Views = 'map' | 'list';

type ViewTriggerButtonPositions = 'sticky' | 'absolute';

type LeLetterstters =
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z';

interface BranchListingProps {
  branches: Kfh_BranchTypeConnection;
}

interface StyledStickyViewToggleButtonProps {
  stickyViewTogglePosition: ViewTriggerButtonPositions;
  stickyViewToggleVisibility: boolean;
}

// Styles

const StyledMapView = styled.div`
  position: relative;
`;

const StyledForm = styled(Panel)`
  ${({ theme: { space, media, maxMedia, layers } }) => css`
    @media (max-width: ${maxMedia.lg}) {
      margin-bottom: ${space.lg};
    }

    // Desktop > styles

    @media (min-width: ${media.lg}) {
      position: absolute;
      top: ${space.xl};
      right: ${space.xl};
      width: 352px;
      z-index: ${layers.branchSearch};
    }
  `};
`;

const StyledBranch = styled.li`
  ${({ theme: { space, colors, media, borders } }) => css`
    display: grid;
    gap: ${space.md};

    a {
      display: flex;

      &:hover {
        color: ${colors.brand.default};
      }

      &[href^='mailto:'] {
        text-decoration: underline;
      }
    }

    & + & {
      margin-top: ${space.lg};
      padding-top: ${space.lg};
      border-top: ${borders.default};
    }

    // Tablet > styles

    @media (min-width: ${media.md}) {
      gap: ${space.lg};
      grid-template-columns: repeat(2, 1fr);
    }

    // Desktop > styles

    @media (min-width: ${media.xl}) {
      grid-template-columns: 400px 1fr 1fr;
    }
  `};
`;

const StyledBranchHeader = styled(Spacer)`
  ${({ theme: { space, media } }) => css`
    // Tablet > styles

    @media (min-width: ${media.md}) {
      padding-right: ${space.lg};
    }

    // Desktop > styles

    @media (min-width: ${media.xl}) {
      /* grid-template-columns: repeat(3, 1fr); */
    }
  `};
`;

const StyledLetterNavigation = styled.nav`
  ${({ theme: { space, media } }) => css`
    margin-bottom: ${space.lg};
    overflow-y: auto;

    ul {
      display: flex;
      gap: ${space.sm};
    }

    // Tablet > styles

    @media (min-width: ${media.md}) {
      overflow-y: initial;
      max-width: 808px;
      ul {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));

        button {
          width: 100%;
          padding-right: 0;
          padding-left: 0;
        }
      }
    }
  `};
`;

const StyledViewToggle = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    margin-bottom: ${space.lg};

    // Tablet < styles

    @media (max-width: ${maxMedia.md}) {
      display: none;
    }
  `};
`;

const StyledStickyViewToggleButton = styled(Button)<StyledStickyViewToggleButtonProps>`
  ${({ theme: { space, media, shadows }, stickyViewTogglePosition, stickyViewToggleVisibility }) => css`
    opacity: ${stickyViewToggleVisibility ? '1' : '0'};
    position: ${stickyViewTogglePosition};
    bottom: ${space.lg};
    left: 50%;
    transform: translateX(-50%);
    margin-top: ${space.lg};
    width: 164px;
    box-shadow: ${shadows.small};
    transition: all 0.3s ease-in-out;

    // Tablet > styles

    @media (min-width: ${media.md}) {
      display: none;
    }
  `};
`;

const StyledStickyViewContainer = styled.div`
  position: relative;
`;

const iconComponent = BranchMapMarker;

export const BranchFinder: FC<Kfh_UmbracoBranchFinderComponentType> = ({ ...rest }) => {
  const { branchList } = useStaticQuery(graphql`
    query branchesQuery {
      branchList: allKfhBranchType(sort: { name: ASC }) {
        nodes {
          ...BranchListingDetails
        }
      }
    }
  `);

  // console.log(branchList);

  const { setActiveBlade } = useContext(UiStateContext);
  const [activeBranch, setActiveBranch] = useState();
  const [searchedLocation, setSearchedLocation] = useState<string>();
  const [activeView, setActiveView] = useState<Views>('list');
  const [stickyViewTogglePosition, setStickyViewTogglePosition] = useState<ViewTriggerButtonPositions>('sticky');
  const [stickyViewToggleVisibility, setStickyViewToggleVisibility] = useState<boolean>(true);
  const stickyViewContainerRef = useRef<HTMLDivElement>(null);
  const stickyViewToggleRef = useRef<HTMLDivElement>(null);
  const { map } = useContext(MapContext);
  const { dataLayerPush } = useDataLayerPush();

  // Branches
  // If sales and letting are at different location then we need to divide the data

  const branches: Kfh_BranchType[] = useMemo(
    () =>
      branchList.nodes.reduce((previousValue, currentValue) => {
        if (
          currentValue.isLettings &&
          currentValue.salesLat !== currentValue.lettingsLat &&
          currentValue.salesLon !== currentValue.lettingsLon
        ) {
          const lettingsBranch = {
            ...currentValue,
          };
          delete lettingsBranch.isSales;

          previousValue.push(lettingsBranch);

          const salesBranch = {
            ...currentValue,
          };
          delete salesBranch.isLettings;

          previousValue.push(salesBranch);
        } else {
          previousValue.push(currentValue);
        }
        return previousValue;
      }, []),
    [branchList]
  );

  const [activeBranches, setActiveBranches] = useState(branches);

  // Search form

  const formMethods = useForm<{ location: string }>({
    mode: 'onSubmit',
  });

  const { register, handleSubmit, control, formState, reset } = formMethods;
  const { errors, dirtyFields, touchedFields } = formState;
  const [getBranch, { loading, error, data: branchData }] = useGet_BranchLazyQuery();

  useEffect(() => {
    // If we have data(found a branch) and its not loading or state of error
    if (branchData?.branch?.branchFinder && (!error || !loading)) {
      const branchId = `${branchData.branch.branchFinder.__typename}:${branchData.branch.branchFinder.id}`;
      // Filter branches

      const filteredBranches = branches.filter((branchNode) => branchNode.id === branchId);
      // Set the active branches (for the map only)

      setActiveBranches(filteredBranches);

      // Update data layer

      dataLayerPush({
        event: 'branchFinderMap',
        branchFinderSearchTerm: searchedLocation,
        branchFinderSearchResults: filteredBranches.length,
        formValidation: 'success',
        formError: undefined,
      });
    }

    // No results
    if (branchData && !branchData.branch.branchFinder && (!error || !loading)) {
      dataLayerPush({
        event: 'branchFinderMap',
        branchFinderSearchTerm: searchedLocation,
        branchFinderSearchResults: 0,
        formValidation: 'error',
        formError: undefined,
      });
    }
  }, [branchData, branches, error, loading, map, searchedLocation]);

  // Search form submit

  const onSubmit = (formData) => {
    const { location } = formData;

    // get Branch

    getBranch({ variables: { location } });

    // Set the searched location

    setSearchedLocation(location);
  };

  const handleClearForm = () => {
    setActiveBranches(branches);
    setSearchedLocation(undefined);
    reset();
  };

  // Setting the mobile view toggle states (visible/position) based on scroll

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (stickyViewContainerRef.current) {
        const containerHeight = stickyViewContainerRef.current?.offsetHeight;
        const windowHeight = window.innerHeight;

        if (entry.isIntersecting) {
          setStickyViewTogglePosition('absolute');

          if (containerHeight > windowHeight) {
            setStickyViewToggleVisibility(false);
          }
        } else {
          setStickyViewTogglePosition('sticky');
          setStickyViewToggleVisibility(true);
        }
      }
    });
    if (stickyViewToggleRef.current) {
      observer.observe(stickyViewToggleRef.current);
    }
  }, [stickyViewToggleRef]);

  // Map markers

  const mapMarkers: MapMarker[] = useMemo(() => {
    const handleOpenBlade = (branch) => {
      setActiveBranch(branch);
      setActiveBlade('branch');

      dataLayerPush({
        event: 'map pin click',
        clickType: 'KFH Branch',
        clickHostname: undefined,
        clickPath: undefined,
        clickQueryString: undefined,
        clickBladeId: 'branch',
      });
    };

    const newBranches = activeBranches
      .map((branch) => {
        // Stop marker being generated if no lat/long
        if (
          (!branch.isLettings && !branch.salesLat && !branch.salesLon) ||
          (branch.isLettings && !branch.lettingsLat && !branch.lettingsLon)
        ) {
          return;
        }

        return {
          iconComponent,
          id: branch.id,
          position: branch.isLettings
            ? { lat: branch.lettingsLat, lng: branch.lettingsLon }
            : { lat: branch.salesLat, lng: branch.salesLon },
          onClick: () => handleOpenBlade(branch),
        } as MapMarker;
      })
      .filter((marker) => !!marker) as MapMarker[];

    return newBranches;
  }, [activeBranches, setActiveBlade]);

  // View toggle

  const handleViewToggleClick = (view: Views) => {
    setActiveView(view);
    updateCallTracking();

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  // Alphabet navigation

  const alphabetLetters = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ];

  const letterRefs = alphabetLetters.reduce((acc, value) => {
    acc[value] = React.createRef();
    return acc;
  }, {});

  const handleLetterNavigationClick = (letter: Letters) => {
    letterRefs[letter].current.scrollIntoView({
      behavior: 'smooth',
    });
  };

  return (
    <FormProvider {...formMethods}>
      <Section contained>
        <MapContextProvider>
          {/* View toggle */}

          <StyledViewToggle>
            {activeView === 'list' && (
              <Button
                type="button"
                variant="pill"
                iconLeft={<MapView />}
                label="Map view"
                onClick={() => handleViewToggleClick('map')}
              />
            )}
            {activeView === 'map' && (
              <Button
                type="button"
                variant="pill"
                iconLeft={<ListView />}
                label="List view"
                onClick={() => handleViewToggleClick('list')}
              />
            )}
          </StyledViewToggle>

          <StyledStickyViewContainer ref={stickyViewContainerRef}>
            {/* Branch Map view */}
            {activeView === 'map' && (
              <StyledMapView>
                <StyledForm
                  heading="Search for a branch"
                  forwardedAs="form"
                  onSubmit={handleSubmit(onSubmit)}
                  method="post"
                  noValidate
                  shadow
                  backgroundColor="white"
                >
                  {loading ? (
                    <Loader centered />
                  ) : (
                    <Spacer spacing="half">
                      <Input
                        type="text"
                        invalid={!!errors.location}
                        valid={touchedFields.location && dirtyFields.location && !errors.location}
                        label="Location"
                        id="location"
                        placeholder="Enter location or postcode"
                        {...register('location', { required: true })}
                        required
                        hideLabel
                        displayClearButton={touchedFields.location}
                        clearOnClick={() => handleClearForm()}
                      />
                      <Button variant="solid" type="submit" disableClickTracking iconRight={<Search />} fullWidth>
                        Search
                      </Button>
                      {branchData && !branchData.branch.branchFinder && searchedLocation ? (
                        <Alert text={`Sorry, we couldn't find a branch for ${searchedLocation}`} />
                      ) : (
                        ''
                      )}
                    </Spacer>
                  )}
                </StyledForm>
                <Map markers={mapMarkers.filter((x) => x !== undefined)} mapStyle="branches" defaultZoom={11} />
                {activeBranch && (
                  <BranchBlade
                    branch={activeBranch}
                    prioritiseLettings={activeBranch?.isLettings && !activeBranch?.isSales}
                  />
                )}
              </StyledMapView>
            )}

            {/* Branch List view */}

            {activeView === 'list' && (
              <>
                {/* Letter navigation */}
                <StyledLetterNavigation>
                  <ul>
                    {alphabetLetters.map((letter, index: number) => {
                      const letterBranches = branches.filter((branch) => branch.name.startsWith(letter));
                      return (
                        <li key={`letters-${index}`}>
                          <Button
                            as="button"
                            type="button"
                            variant="pill"
                            disabled={letterBranches.length === 0}
                            onClick={() => handleLetterNavigationClick(letter)}
                          >
                            {letter}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </StyledLetterNavigation>
                {/* Branch list */}
                <ExpanderPanelGroup as="ul">
                  {alphabetLetters.map((letter) => {
                    const letterBranches = branchList.nodes.filter((branch) => branch.name.startsWith(letter));
                    const isDisabled = letterBranches.length === 0;

                    return (
                      <ExpanderPanel
                        key={letter}
                        as="li"
                        title={letter}
                        open={!isDisabled}
                        headingComponent="h2"
                        disabled={isDisabled}
                        ref={letterRefs[letter]}
                      >
                        <ul>
                          {letterBranches.map((branch, index: number) => {
                            const { businessProfile } = branch;
                            const salesContactDetails = businessProfile?.salesContactDetails;
                            const lettingsContactDetails = businessProfile?.lettingsContactDetails;

                            return (
                              <StyledBranch key={`branch-${index}`}>
                                <StyledBranchHeader spacing="quarter">
                                  <Typography component="h3" variant="titleLarge">
                                    {branch.name} {branch.isSales && !branch.isLettings ? 'Sales' : ' '}{' '}
                                    {!branch.isSales && branch.isLettings ? 'Lettings' : ''}
                                  </Typography>
                                  {branch.relativeUrl && (
                                    <IconLink url={branch.relativeUrl} arrowRight label="View branch details page" />
                                  )}
                                </StyledBranchHeader>

                                {branch.isSales && (
                                  <div>
                                    <Typography component="h4" variant="titleSmall">
                                      Sales
                                    </Typography>
                                    {salesContactDetails?.telephone && (
                                      <a href={`tel:${salesContactDetails?.telephone?.replace(/\s/g, '')}`}>
                                        {salesContactDetails?.telephone}
                                      </a>
                                    )}
                                    {salesContactDetails?.email && (
                                      <a href={`mailto:${salesContactDetails?.email}`}>{salesContactDetails?.email}</a>
                                    )}
                                  </div>
                                )}
                                {branch.isLettings && (
                                  <div>
                                    <Typography component="h4" variant="titleSmall">
                                      Lettings
                                    </Typography>
                                    {lettingsContactDetails?.telephone && (
                                      <a href={`tel:${lettingsContactDetails?.telephone?.replace(/\s/g, '')}`}>
                                        {lettingsContactDetails?.telephone}
                                      </a>
                                    )}
                                    {lettingsContactDetails?.email && (
                                      <a href={`mailto:${lettingsContactDetails?.email}`}>
                                        {lettingsContactDetails?.email}
                                      </a>
                                    )}
                                  </div>
                                )}
                              </StyledBranch>
                            );
                          })}
                        </ul>
                      </ExpanderPanel>
                    );
                  })}
                </ExpanderPanelGroup>
              </>
            )}

            {/* Sticky View toggle */}
            <div ref={stickyViewToggleRef} />
            {activeView === 'list' && (
              <StyledStickyViewToggleButton
                type="button"
                variant="solid"
                iconRight={<MapView />}
                label="Map view"
                onClick={() => handleViewToggleClick('map')}
                stickyViewTogglePosition={stickyViewTogglePosition}
                stickyViewToggleVisibility={stickyViewToggleVisibility}
              />
            )}
            {activeView === 'map' && (
              <StyledStickyViewToggleButton
                type="button"
                variant="solid"
                iconRight={<ListView />}
                label="List view"
                onClick={() => handleViewToggleClick('list')}
                stickyViewTogglePosition={stickyViewTogglePosition}
                stickyViewToggleVisibility={stickyViewToggleVisibility}
              />
            )}
          </StyledStickyViewContainer>
        </MapContextProvider>
      </Section>
    </FormProvider>
  );
};
